/**
 * KPI Calculations - TTM and YTD metrics
 */

import { V2ChartData } from '../services/V2DataService';
import { CategoryApiResponse } from '../services/CategoryDataService';

export interface KPIMetrics {
  ttmFunding: {
    label: string;
    value: number;
    count: number;
    previousValue: number;
    previousCount: number;
    period: string;
    type: 'currency';
  };
  ttmMA: {
    label: string;
    value: number;
    count: number;
    previousValue: number;
    previousCount: number;
    period: string;
    type: 'count';
  };
  ytdFunding: {
    label: string;
    value: number;
    count: number;
    previousValue: number;
    previousCount: number;
    period: string;
    type: 'currency';
  };
  ytdMA: {
    label: string;
    value: number;
    count: number;
    previousValue: number;
    previousCount: number;
    period: string;
    type: 'count';
  };
  topFundedCategory: {
    label: string;
    value: number;
    name: string;
    count: number;
    period: string;
    type: 'currency';
  };
  topAcquiredCategory: {
    label: string;
    value: number;
    name: string;
    count: number;
    period: string;
    type: 'count';
  };
  ttmTopFundedCategory: {
    label: string;
    value: number;
    name: string;
    count: number;
    period: string;
    type: 'currency';
  };
  ttmTopAcquiredCategory: {
    label: string;
    value: number;
    name: string;
    count: number;
    period: string;
    type: 'count';
  };
  // New deal economics metrics
  ttmAverageDealSize: {
    label: string;
    value: number;
    previousValue: number;
    period: string;
    type: 'currency';
  };
  ytdAverageDealSize: {
    label: string;
    value: number;
    previousValue: number;
    period: string;
    type: 'currency';
  };
  // M&A Dollar Values - total disclosed M&A transaction values
  ttmMADollarValue: {
    label: string;
    value: number;
    count: number;
    previousValue: number;
    previousCount: number;
    period: string;
    type: 'currency';
  };
  ytdMADollarValue: {
    label: string;
    value: number;
    count: number;
    previousValue: number;
    previousCount: number;
    period: string;
    type: 'currency';
  };
}

/**
 * Calculate KPI metrics from chart data
 * Now accepts separate TTM and YTD datasets, plus TTM category data
 */
export function calculateKPIMetrics(
  ttmFundingData: V2ChartData,
  ttmMAData: V2ChartData,
  ytdFundingData: V2ChartData,
  ytdMAData: V2ChartData,
  productCategoryData?: CategoryApiResponse,
  acquisitionCategoryData?: CategoryApiResponse,
  ttmProductCategoryData?: CategoryApiResponse,
  ttmAcquisitionCategoryData?: CategoryApiResponse
): KPIMetrics {
  const currentYear = new Date().getFullYear();

  // TTM Metrics - use TTM dataset
  // Prefer summary field (more accurate with filters), fallback to totals + series aggregation
  const ttmFundingValue = ttmFundingData.summary?.current_amount ?? ttmFundingData.totals.current;
  const ttmFundingPrevious = ttmFundingData.summary?.previous_amount ?? ttmFundingData.totals.previous;
  const ttmFundingDeals = ttmFundingData.summary?.current_deals ?? (ttmFundingData.series[0]?.deal_counts?.reduce((a, b) => a + b, 0) || 0);
  const ttmFundingPreviousDeals = ttmFundingData.summary?.previous_deals ?? (ttmFundingData.series[1]?.deal_counts?.reduce((a, b) => a + b, 0) || 0);

  const ttmMADeals = ttmMAData.summary?.current_count ?? ttmMAData.totals.current;
  const ttmMAPrevious = ttmMAData.summary?.previous_count ?? ttmMAData.totals.previous;
  const ttmMAPreviousDeals = ttmMAData.series[1]?.data?.reduce((a, b) => a + b, 0) || 0;

  // YTD Metrics - use YTD dataset
  // Prefer summary field (more accurate with filters), fallback to series aggregation
  const ytdFundingValue = ytdFundingData.summary?.current_amount ??
    (ytdFundingData.series[ytdFundingData.series.length - 1]?.data.reduce((a, b) => a + b, 0) || 0);
  const ytdFundingDeals = ytdFundingData.summary?.current_deals ??
    (ytdFundingData.series[ytdFundingData.series.length - 1]?.deal_counts?.reduce((a, b) => a + b, 0) || 0);

  // Previous year YTD (second-to-last series for comparison)
  const ytdFundingPreviousValue = ytdFundingData.summary?.previous_amount ??
    (ytdFundingData.series[ytdFundingData.series.length - 2]?.data.reduce((a, b) => a + b, 0) || 0);
  const ytdFundingPreviousDeals = ytdFundingData.summary?.previous_deals ??
    (ytdFundingData.series[ytdFundingData.series.length - 2]?.deal_counts?.reduce((a, b) => a + b, 0) || 0);

  // For M&A: data array contains deal counts, not dollar values
  // Prefer summary field (more accurate with filters), fallback to series aggregation
  const ytdMADeals = ytdMAData.summary?.current_count ??
    (ytdMAData.series[ytdMAData.series.length - 1]?.data.reduce((a, b) => a + b, 0) || 0);

  const ytdMAPreviousDeals = ytdMAData.summary?.previous_count ??
    (ytdMAData.series[ytdMAData.series.length - 2]?.data.reduce((a, b) => a + b, 0) || 0);

  // Category metrics - YTD
  const topFundedCat = productCategoryData?.categories?.[0];
  const topAcquiredCat = acquisitionCategoryData?.categories?.[0];

  // Category metrics - TTM
  const ttmTopFundedCat = ttmProductCategoryData?.categories?.[0];
  const ttmTopAcquiredCat = ttmAcquisitionCategoryData?.categories?.[0];

  // Calculate average deal sizes - deal economics metric
  const ttmAvgDealSize = ttmFundingDeals > 0 ? ttmFundingValue / ttmFundingDeals : 0;
  const ttmPrevAvgDealSize = ttmFundingPreviousDeals > 0 ? ttmFundingPrevious / ttmFundingPreviousDeals : 0;

  const ytdAvgDealSize = ytdFundingDeals > 0 ? ytdFundingValue / ytdFundingDeals : 0;
  const ytdPrevAvgDealSize = ytdFundingPreviousDeals > 0 ? ytdFundingPreviousValue / ytdFundingPreviousDeals : 0;

  // Calculate M&A dollar values - total disclosed M&A transaction values
  // Note: M&A data may have amounts in summary or we sum from series data
  const ttmMAValue = ttmMAData.summary?.current_amount ??
    (ttmMAData.series[ttmMAData.series.length - 1]?.data.reduce((a, b) => a + b, 0) || 0);
  const ttmMAPreviousValue = ttmMAData.summary?.previous_amount ??
    (ttmMAData.series[ttmMAData.series.length - 2]?.data.reduce((a, b) => a + b, 0) || 0);

  const ytdMAValue = ytdMAData.summary?.current_amount ??
    (ytdMAData.series[ytdMAData.series.length - 1]?.data.reduce((a, b) => a + b, 0) || 0);
  const ytdMAPreviousValue = ytdMAData.summary?.previous_amount ??
    (ytdMAData.series[ytdMAData.series.length - 2]?.data.reduce((a, b) => a + b, 0) || 0);

  return {
    ttmFunding: {
      label: 'TTM Funding',
      value: ttmFundingValue,
      count: ttmFundingDeals,
      previousValue: ttmFundingPrevious,
      previousCount: ttmFundingPreviousDeals,
      period: 'prior TTM',
      type: 'currency'
    },
    ttmMA: {
      label: 'TTM M&A',
      value: ttmMADeals,
      count: ttmMADeals, // For M&A, the value IS the count
      previousValue: ttmMAPrevious,
      previousCount: ttmMAPreviousDeals,
      period: 'prior TTM',
      type: 'count'
    },
    ytdFunding: {
      label: `YTD ${currentYear} Funding`,
      value: ytdFundingValue,
      count: ytdFundingDeals,
      previousValue: ytdFundingPreviousValue,
      previousCount: ytdFundingPreviousDeals,
      period: `YTD ${currentYear - 1}`,
      type: 'currency'
    },
    ytdMA: {
      label: `YTD ${currentYear} M&A`,
      value: ytdMADeals,
      count: ytdMADeals, // For M&A, the value IS the count
      previousValue: ytdMAPreviousDeals,
      previousCount: ytdMAPreviousDeals, // For M&A, value IS the count
      period: `YTD ${currentYear - 1}`,
      type: 'count'
    },
    topFundedCategory: {
      label: `YTD ${currentYear} Top Funded Category`,
      value: topFundedCat?.amount || 0,
      name: topFundedCat?.name || 'N/A',
      count: topFundedCat?.count || 0,
      period: `YTD ${currentYear}`,
      type: 'currency'
    },
    topAcquiredCategory: {
      label: `YTD ${currentYear} Top Acquired Category`,
      value: topAcquiredCat?.count || 0,
      name: topAcquiredCat?.name || 'N/A',
      count: topAcquiredCat?.count || 0,
      period: `YTD ${currentYear}`,
      type: 'count'
    },
    ttmTopFundedCategory: {
      label: 'TTM Top Funded Category',
      value: ttmTopFundedCat?.amount || 0,
      name: ttmTopFundedCat?.name || 'N/A',
      count: ttmTopFundedCat?.count || 0,
      period: 'TTM',
      type: 'currency'
    },
    ttmTopAcquiredCategory: {
      label: 'TTM Top Acquired Category',
      value: ttmTopAcquiredCat?.count || 0,
      name: ttmTopAcquiredCat?.name || 'N/A',
      count: ttmTopAcquiredCat?.count || 0,
      period: 'TTM',
      type: 'count'
    },
    // New deal economics metrics - Funding specific
    ttmAverageDealSize: {
      label: 'TTM Avg Funding Deal Size',
      value: ttmAvgDealSize,
      previousValue: ttmPrevAvgDealSize,
      period: 'prior TTM',
      type: 'currency'
    },
    ytdAverageDealSize: {
      label: `YTD ${currentYear} Avg Funding Deal Size`,
      value: ytdAvgDealSize,
      previousValue: ytdPrevAvgDealSize,
      period: `YTD ${currentYear - 1}`,
      type: 'currency'
    },
    // M&A Dollar Values - total disclosed M&A transaction values
    ttmMADollarValue: {
      label: 'TTM M&A Dollar Value',
      value: ttmMAValue,
      count: ttmMADeals,
      previousValue: ttmMAPreviousValue,
      previousCount: ttmMAPrevious,
      period: 'prior TTM',
      type: 'currency'
    },
    ytdMADollarValue: {
      label: `YTD ${currentYear} M&A Dollar Value`,
      value: ytdMAValue,
      count: ytdMADeals,
      previousValue: ytdMAPreviousValue,
      previousCount: ytdMAPreviousDeals,
      period: `YTD ${currentYear - 1}`,
      type: 'currency'
    }
  };
}
